import { ErrorMessage, Field, FieldArray, Form, Formik } from "formik";
import moment from "moment";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  AirplaneInFlight,
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  ClipboardText,
  Clock,
  CreditCard,
  Headset,
  Lightning,
  LockSimple,
  SealCheck,
  ShieldCheck,
  Ticket,
  Wallet,
  Warning,
} from "@phosphor-icons/react";
import { formatAsMoney } from "../../../helpers/numbers";
import { useAuth } from "../../../context/AuthContext";
import useGetInternationalOffer from "../../../hooks/useGetInternationalOffer";
import useCreateInternationalOrder from "../../../hooks/useCreateInternationalOrder";
import useGetInternationalOrderTicket from "../../../hooks/useGetInternationalOrderTicket";
import useIntlFlightFeatureFlags from "../../../hooks/useIntlFlightFeatureFlags";
import useKycRequirements from "../../../hooks/useKycRequirements";
import useResolveGeo from "../../../hooks/useResolveGeo";
import {
  FlightSlice,
  IntlCreateOrderPassenger,
  IntlPaymentMethod,
  NormalizedOffer,
} from "../../../@types/intlFlightAggregator";
import AviationAmbientBackground from "../../../components/AviationAmbientBackground";
import IntlKycGate from "../../../components/IntlKycGate";

const STORAGE_KEY = "intl-flight-v2-selected-offer";

// Keep passenger data only in memory during a KYC detour, never localStorage.
// Reloading the page intentionally discards it. Offers are still revalidated.
let kycDraft: {
  userId: string;
  offerId: string;
  passengers: IntlCreateOrderPassenger[];
  paymentMethod: IntlPaymentMethod;
  expiresAt: number;
} | null = null;

interface FormValues {
  passengers: IntlCreateOrderPassenger[];
}

const passengerSchema = Yup.object().shape({
  given_name: Yup.string().required("First name is required"),
  family_name: Yup.string().required("Last name is required"),
  born_on: Yup.string().required("Date of birth is required"),
  gender: Yup.string().oneOf(["m", "f"]).required("Gender is required"),
  title: Yup.string().required("Title is required"),
  email: Yup.string().email("Enter a valid email").required("Email is required"),
  phone_number: Yup.string().required("Phone number is required"),
});

const validationSchema = Yup.object().shape({
  passengers: Yup.array().of(passengerSchema),
});

const fieldClass = (hasError?: boolean) =>
  `rounded-xl border px-4 py-3 w-full bg-white outline-none transition-colors duration-200 ${
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-4 focus:ring-red-50"
      : "border-gray-200 focus:border-visaro-orange focus:ring-4 focus:ring-visaro-orange/10"
  }`;

const fieldLabelClass = "text-sm font-bold text-gray-700";

/**
 * `user.wallet.available_balance` comes back from the profile endpoint as a
 * comma-formatted string (e.g. "4,624,430.00"), not a number — the TS
 * `Wallet` type is aspirational, not the real runtime shape. Needed here
 * (unlike everywhere else that just hands it straight to formatAsMoney)
 * because this is the one place that does numeric comparison against it.
 */
const parseWalletBalance = (value: number | string | undefined | null): number => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const sidebarCardClass =
  "bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-5 flex flex-col gap-4";

/** Compact origin → destination timeline for one leg of an itinerary. */
const LegSummary = ({ slice, label }: { slice: FlightSlice; label?: string }) => {
  const firstSegment = slice.segments[0];
  const lastSegment = slice.segments[slice.segments.length - 1];
  const stopCount = slice.segments.length - 1;

  return (
    <div className="flex flex-col gap-2">
      {label && <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{label}</span>}
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-start min-w-[46px]">
          <span className="text-base font-bold font-aeonic text-deepBlue">{slice.origin ?? "—"}</span>
          <span className="text-[10px] font-bold text-gray-400">
            {firstSegment?.departure_at ? moment(firstSegment.departure_at).format("HH:mm") : "—"}
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center gap-1 min-w-0 px-1">
          <div className="flex items-center w-full gap-1">
            <div className="h-[1px] flex-1 bg-gray-200" />
            <AirplaneInFlight className="size-3 shrink-0 text-visaro-orange" weight="fill" />
            <div className="h-[1px] flex-1 bg-gray-200" />
          </div>
          <span className="text-[9px] text-gray-400 font-medium truncate">
            {stopCount === 0 ? "Direct" : `${stopCount} stop${stopCount > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="flex flex-col items-end min-w-[46px]">
          <span className="text-base font-bold font-aeonic text-deepBlue">{slice.destination ?? "—"}</span>
          <span className="text-[10px] font-bold text-gray-400">
            {lastSegment?.arrival_at ? moment(lastSegment.arrival_at).format("HH:mm") : "—"}
          </span>
        </div>
      </div>
    </div>
  );
};

const trustPoints = [
  { icon: ShieldCheck, text: "Secured, encrypted payment" },
  { icon: Lightning, text: "Instant e-ticket on confirmation" },
  { icon: Headset, text: "24/7 support if plans change" },
];

const PaymentMethodStep = ({
  offer,
  walletBalance,
  paymentMethod,
  onSelectPaymentMethod,
  onBack,
  onConfirm,
  confirming,
  payIn3Enabled,
  onKycLeave,
  offerRefreshing,
}: {
  offer: NormalizedOffer | null;
  walletBalance: number;
  paymentMethod: IntlPaymentMethod;
  onSelectPaymentMethod: (method: IntlPaymentMethod) => void;
  onBack: () => void;
  onConfirm: () => void;
  confirming: boolean;
  payIn3Enabled: boolean;
  onKycLeave: () => void;
  offerRefreshing: boolean;
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { detectedCountry } = useResolveGeo(paymentMethod === "pay_in_3");
  const [residenceAcknowledged, setResidenceAcknowledged] = useState<string | null>(null);
  const locationPair = `${user?.residence_country}:${detectedCountry}`;
  const needsResidenceConfirmation = !!user?.residence_country && !!detectedCountry &&
    user.residence_country !== detectedCountry && residenceAcknowledged !== locationPair;
  const {
    requirements,
    isLoadingRequirements,
    isRequirementsError,
    refetchRequirements,
  } = useKycRequirements(
    "international_flight",
    paymentMethod === "pay_in_3" && payIn3Enabled
  );
  const amountDueNgn = offer?.quote ? Math.ceil(offer.quote.amount) : null;
  const hasSufficientBalance = amountDueNgn !== null && walletBalance >= amountDueNgn;
  // Preview only — the real schedule (exact due dates/amounts) is computed
  // server-side once financing is submitted, this is just an estimate so
  // the customer knows roughly what to expect before confirming.
  const estimatedInstallmentNgn = amountDueNgn !== null ? Math.ceil(amountDueNgn / 3) : null;

  const canConfirm =
    !!offer &&
    !confirming &&
    !offerRefreshing &&
    (paymentMethod === "wallet"
      ? hasSufficientBalance
      : payIn3Enabled &&
        !needsResidenceConfirmation &&
        !isLoadingRequirements &&
        !isRequirementsError &&
        requirements.satisfied);

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-visaro-orange transition-colors duration-200 self-start"
      >
        <ArrowLeft size={16} weight="bold" />
        Back to passenger details
      </button>

      <h2 className="text-lg font-bold text-deepBlue">Choose how to pay</h2>
      {paymentMethod === "pay_in_3" && needsResidenceConfirmation && <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm">
        <p>Your connection appears to be in {detectedCountry}. Do you still live in {user?.residence_country}? Travelling does not change your residence.</p>
        <div className="mt-3 flex flex-wrap gap-4">
          <button type="button" className="font-semibold text-visaro-orange" onClick={() => setResidenceAcknowledged(locationPair)}>I still live in {user?.residence_country}</button>
          <button type="button" className="font-semibold text-visaro-orange" onClick={() => {
            onKycLeave();
            navigate('/home/settings/compliance/residence', { state: { returnTo: window.location.pathname } });
          }}>I have moved</button>
        </div>
      </div>}

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => onSelectPaymentMethod("wallet")}
          className={`text-left rounded-2xl border p-5 flex items-center gap-4 transition-all duration-200 ${
            paymentMethod === "wallet"
              ? "border-visaro-orange ring-4 ring-visaro-orange/10 bg-white"
              : "border-gray-200 bg-white hover:border-gray-300"
          }`}
        >
          <span
            className={`flex items-center justify-center size-11 rounded-full shrink-0 ${
              paymentMethod === "wallet" ? "bg-visaro-orange text-white" : "bg-deepBlue/5 text-deepBlue"
            }`}
          >
            <Wallet size={20} weight="duotone" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-deepBlue">Pay with Wallet</div>
            <div className="text-sm text-gray-400">₦{formatAsMoney(walletBalance)} available</div>
            {amountDueNgn !== null && !hasSufficientBalance && (
              <div className="text-xs font-medium text-red-500 mt-1">
                Insufficient balance — you need ₦{formatAsMoney(amountDueNgn)}. Please top up your wallet.
              </div>
            )}
          </div>
          <span
            className={`shrink-0 size-5 rounded-full border-2 flex items-center justify-center ${
              paymentMethod === "wallet" ? "border-visaro-orange" : "border-gray-300"
            }`}
          >
            {paymentMethod === "wallet" && <span className="size-2.5 rounded-full bg-visaro-orange" />}
          </span>
        </button>

        {payIn3Enabled ? (
          <button
            type="button"
            onClick={() => onSelectPaymentMethod("pay_in_3")}
            className={`text-left rounded-2xl border p-5 flex items-center gap-4 transition-all duration-200 ${
              paymentMethod === "pay_in_3"
                ? "border-visaro-orange ring-4 ring-visaro-orange/10 bg-white"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <span
              className={`flex items-center justify-center size-11 rounded-full shrink-0 ${
                paymentMethod === "pay_in_3" ? "bg-visaro-orange text-white" : "bg-deepBlue/5 text-deepBlue"
              }`}
            >
              <CreditCard size={20} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-deepBlue">Pay in 3</div>
              <div className="text-sm text-gray-400">
                {estimatedInstallmentNgn !== null
                  ? `≈ 3 payments of ₦${formatAsMoney(estimatedInstallmentNgn)}`
                  : "Split the cost into 3 payments"}
              </div>
            </div>
            <span
              className={`shrink-0 size-5 rounded-full border-2 flex items-center justify-center ${
                paymentMethod === "pay_in_3" ? "border-visaro-orange" : "border-gray-300"
              }`}
            >
              {paymentMethod === "pay_in_3" && <span className="size-2.5 rounded-full bg-visaro-orange" />}
            </span>
          </button>
        ) : (
          <div className="text-left rounded-2xl border border-gray-200 bg-gray-50 p-5 flex items-center gap-4 opacity-60 cursor-not-allowed">
            <span className="flex items-center justify-center size-11 rounded-full bg-gray-200 text-gray-400 shrink-0">
              <CreditCard size={20} weight="duotone" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-500">Pay in 3</div>
              <div className="text-sm text-gray-400">Split the cost into 3 payments</div>
            </div>
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-200 px-2.5 py-1 rounded-full whitespace-nowrap">
              Coming soon
            </span>
          </div>
        )}
      </div>

      {paymentMethod === "pay_in_3" && (
        <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Clock size={14} weight="fill" className="shrink-0" />
          Your financing request needs to be approved before your flight is booked — this usually
          doesn't take long, but it isn't instant like paying with your wallet.
        </div>
      )}

      {paymentMethod === "pay_in_3" && isLoadingRequirements && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
          <HashLoader size={20} color="#EA5C29" />
          Checking your KYC requirements...
        </div>
      )}

      {paymentMethod === "pay_in_3" && isRequirementsError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>We could not check your KYC. Try again, or choose wallet payment to continue without financing.</p>
          <button
            type="button"
            onClick={() => refetchRequirements()}
            className="mt-3 font-semibold text-visaro-orange"
          >
            Try again
          </button>
        </div>
      )}

      {paymentMethod === "pay_in_3" &&
        !isLoadingRequirements &&
        !isRequirementsError &&
        !requirements.satisfied && (
          <IntlKycGate
            requirements={requirements}
            onRecheck={() => refetchRequirements()}
            onLeave={onKycLeave}
          />
        )}

      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <LockSimple size={12} weight="fill" />
        Payments are processed securely
      </div>

      <button
        type="button"
        onClick={onConfirm}
        disabled={!canConfirm}
        className="bg-visaro-orange text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-visaro-orange/90 disabled:opacity-50 disabled:hover:bg-visaro-orange transition-colors duration-200"
      >
        {confirming
          ? paymentMethod === "wallet"
            ? "Booking..."
            : "Submitting..."
          : paymentMethod === "wallet"
          ? amountDueNgn !== null
            ? `Pay ₦${formatAsMoney(amountDueNgn)} now`
            : "Confirm & Pay"
          : "Submit financing request"}
      </button>
    </div>
  );
};

/**
 * Revalidates the selected offer (mandatory before showing a final price —
 * see FlightSupplierInterface::getOffer), collects passenger details, and
 * books it. Pays the supplier from Visaro's own balance; does not yet debit
 * the customer or attach a loan — see backend scope notes on
 * InternationalFlightAggregatorController::createOrder.
 */
const ReviewAndBook = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { payIn3Enabled } = useIntlFlightFeatureFlags();
  const [copied, setCopied] = useState(false);

  const initialOffer: NormalizedOffer | null = useMemo(() => {
    if (state?.offer) return state.offer as NormalizedOffer;
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? (JSON.parse(stored) as NormalizedOffer) : null;
    } catch {
      return null;
    }
  }, [state?.offer]);

  const [restoredDraft] = useState(() => {
    const draft = kycDraft;
    return draft && draft.userId === String(user?.id) &&
      draft.offerId === initialOffer?.id && draft.expiresAt > Date.now()
      ? draft : null;
  });
  const [step, setStep] = useState<"passengers" | "payment">(restoredDraft ? "payment" : "passengers");
  const [passengerValues, setPassengerValues] = useState<IntlCreateOrderPassenger[] | null>(restoredDraft?.passengers ?? null);
  const [paymentMethod, setPaymentMethod] = useState<IntlPaymentMethod>(restoredDraft?.paymentMethod ?? "wallet");
  useEffect(() => { kycDraft = null; }, []);

  useEffect(() => {
    if (state?.offer) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.offer));
    }
  }, [state?.offer]);

  const { offer: refreshedOffer, refreshOffer, isRefreshingOffer, offerError } = useGetInternationalOffer(
    initialOffer?.id,
    { enabled: !!initialOffer?.id }
  );

  const offer = refreshedOffer ?? initialOffer;

  const { createOrder, createOrderResult, creatingOrder, isCreateOrderError, createOrderError } =
    useCreateInternationalOrder();

  const bookedId = createOrderResult?.data?.booking
    ? String((createOrderResult.data.booking as Record<string, unknown>).id ?? "")
    : undefined;

  const { ticket, fetchTicket, isFetchingTicket, hasFetchedTicket } = useGetInternationalOrderTicket(bookedId, {
    enabled: false,
  });

  useEffect(() => {
    if (bookedId) {
      fetchTicket();
    }
    // Only re-fetch when the booking itself changes, not on every render —
    // `fetchTicket` is otherwise a "Check again" action triggered manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookedId]);

  if (!initialOffer) {
    return (
      <div className="min-h-screen bg-gray-50 p-10 flex flex-col items-center justify-center gap-4 text-center">
        <p className="text-gray-500">No flight selected. Start a new search.</p>
        <button
          type="button"
          onClick={() => navigate("/aviation/international/v2/search")}
          className="bg-visaro-orange text-white px-6 py-3 rounded-xl font-semibold"
        >
          Search Flights
        </button>
      </div>
    );
  }

  const slice = offer?.slices?.[0];
  const firstSegment = slice?.segments[0];
  const lastSegment = slice?.segments[slice.segments.length - 1];
  const passengerPlaceholders = offer?.passenger_placeholders ?? [];

  if (createOrderResult?.data?.plan) {
    const { plan } = createOrderResult.data;

    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center p-5 py-14">
        <AviationAmbientBackground />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="relative w-full max-w-md"
        >
          <div className="flex flex-col items-center text-center gap-3 mb-6">
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.15 }}
              className="relative flex items-center justify-center size-16 rounded-full bg-amber-100"
            >
              <Clock size={30} weight="fill" className="text-amber-600" />
            </motion.span>
            <h1 className="text-2xl font-bold text-deepBlue">Financing request submitted</h1>
            <p className="text-gray-500 max-w-xs">
              We've sent your Pay in 3 request for approval. Your flight will be booked
              automatically once it's confirmed — this page doesn't need to stay open.
            </p>
          </div>

          <div className={sidebarCardClass}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Route</span>
              <span className="text-sm font-semibold text-deepBlue flex items-center gap-1.5">
                {slice?.origin}
                <AirplaneInFlight size={12} weight="fill" className="text-visaro-orange" />
                {slice?.destination}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Total</span>
              <span className="text-sm font-semibold text-deepBlue">₦{formatAsMoney(plan.total_amount_ngn)}</span>
            </div>
            <div className="h-px bg-gray-100" />
            <div className="flex flex-col gap-2">
              {plan.schedule.map((installment, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    Installment {index + 1} · {moment(installment.due_date).format("D MMM YYYY")}
                  </span>
                  <span className="font-semibold text-deepBlue">₦{formatAsMoney(installment.amount_ngn)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-6">
            <button
              type="button"
              onClick={() => navigate("/aviation/your-flights", { state: { tab: "international" } })}
              className="w-full bg-visaro-orange text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-visaro-orange/90 transition-colors duration-200"
            >
              View your flights
            </button>
            <button
              type="button"
              onClick={() => navigate("/aviation")}
              className="w-full text-visaro-orange font-medium py-2 hover:text-visaro-orange/80 transition-colors duration-200"
            >
              Back to Aviation
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (createOrderResult?.data?.order) {
    const { order } = createOrderResult.data;

    const onCopyReference = () => {
      if (!order.booking_reference) return;
      navigator.clipboard.writeText(order.booking_reference);
      setCopied(true);
      toast.success("Booking reference copied");
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center p-5 py-14">
        <AviationAmbientBackground />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="relative w-full max-w-md"
        >
          <div className="flex flex-col items-center text-center gap-3 mb-6">
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 15, delay: 0.15 }}
              className="relative flex items-center justify-center size-16 rounded-full bg-visaro-visaMintGreen"
            >
              <span className="absolute inset-0 rounded-full bg-visaro-visaGreen/30 animate-ping" />
              <CheckCircle size={34} weight="fill" className="relative text-visaro-visaGreen" />
            </motion.span>
            <h1 className="text-2xl font-bold text-deepBlue">Flight booked!</h1>
            <p className="text-gray-500 max-w-xs">
              Your booking is confirmed for {passengerPlaceholders.length} passenger
              {passengerPlaceholders.length === 1 ? "" : "s"}. Details have been saved to your trips.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_8px_30px_-8px_rgba(15,23,42,0.18)] border border-gray-100 overflow-hidden">
            {/* Route header */}
            <div className="p-6 flex items-center justify-between bg-deepBlue text-white">
              <div className="flex flex-col">
                <span className="text-2xl font-bold font-aeonic">{slice?.origin ?? "—"}</span>
                {firstSegment?.departure_at && (
                  <span className="text-xs text-white/60 font-medium">
                    {moment(firstSegment.departure_at).format("HH:mm, D MMM")}
                  </span>
                )}
              </div>
              <AirplaneInFlight size={20} weight="fill" className="text-visaro-orange shrink-0" />
              <div className="flex flex-col items-end">
                <span className="text-2xl font-bold font-aeonic">{slice?.destination ?? "—"}</span>
                {lastSegment?.arrival_at && (
                  <span className="text-xs text-white/60 font-medium">
                    {moment(lastSegment.arrival_at).format("HH:mm, D MMM")}
                  </span>
                )}
              </div>
            </div>

            {/* Perforated divider */}
            <div className="relative border-t-2 border-dashed border-gray-200">
              <div className="absolute -left-3 -top-3 size-6 bg-gray-50 rounded-full" />
              <div className="absolute -right-3 -top-3 size-6 bg-gray-50 rounded-full" />
            </div>

            <div className="p-6 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Booking reference</span>
                <button
                  type="button"
                  onClick={onCopyReference}
                  className="group flex items-center gap-1.5 text-lg font-bold font-aeonic text-deepBlue tracking-widest hover:text-visaro-orange transition-colors duration-200"
                >
                  {order.booking_reference}
                  {copied ? (
                    <Check size={14} weight="bold" className="text-visaro-visaGreen" />
                  ) : (
                    <ClipboardText size={14} className="text-gray-300 group-hover:text-visaro-orange transition-colors duration-200" />
                  )}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Airline</span>
                <span className="text-sm font-semibold text-deepBlue">
                  {firstSegment?.airline ?? "—"} {firstSegment?.flight_number ?? ""}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Total paid</span>
                <span className="text-sm font-semibold text-deepBlue">
                  {order.currency} {order.total_amount !== null ? formatAsMoney(order.total_amount) : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Status</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-visaro-visaGreen bg-visaro-visaMintGreen px-2.5 py-1 rounded-full">
                  <SealCheck size={12} weight="fill" />
                  {order.status}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-5 mt-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ticket size={16} weight="duotone" className="text-visaro-orange" />
                <h2 className="text-sm font-bold text-deepBlue">Your e-ticket</h2>
              </div>
              {hasFetchedTicket && (
                <button
                  type="button"
                  onClick={() => fetchTicket()}
                  disabled={isFetchingTicket}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-visaro-orange hover:text-visaro-orange/80 disabled:opacity-50 transition-colors duration-200"
                >
                  <ArrowClockwise size={12} weight="bold" className={isFetchingTicket ? "animate-spin" : ""} />
                  Check again
                </button>
              )}
            </div>

            {isFetchingTicket && !hasFetchedTicket ? (
              <p className="text-xs text-gray-400">Checking with the airline…</p>
            ) : ticket && ticket.documents.length > 0 ? (
              <div className="flex flex-col gap-2">
                {ticket.documents.map((doc, index) => (
                  <div key={doc.unique_identifier + index} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">E-ticket number</span>
                    <span className="font-bold font-aeonic text-deepBlue tracking-wide">{doc.unique_identifier}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Your e-ticket is still being issued by the airline — this usually takes a few minutes. Check back
                here, or quote your booking reference directly with the airline.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 mt-6">
            <button
              type="button"
              onClick={() => navigate("/aviation/your-flights", { state: { tab: "international" } })}
              className="w-full bg-visaro-orange text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-visaro-orange/90 transition-colors duration-200"
            >
              View your flights
            </button>
            <button
              type="button"
              onClick={() => navigate("/aviation/international/v2/search")}
              className="w-full text-visaro-orange font-medium py-2 hover:text-visaro-orange/80 transition-colors duration-200"
            >
              Book another flight
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const taxAmount = offer?.tax_amount;
  const baseAmount = offer?.base_amount;

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <AviationAmbientBackground />

      <div className="relative p-5 lg:p-10 py-10 flex flex-col gap-6 max-w-5xl mx-auto">
        {(isRefreshingOffer || creatingOrder) && (
          <div className="fixed top-0 z-50 left-0 h-screen w-screen flex flex-col items-center justify-center gap-3 bg-black/20 backdrop-blur-sm">
            <HashLoader color="#EA5C29" />
            <span className="text-white font-medium text-sm">
              {creatingOrder ? "Booking your flight…" : "Refreshing price…"}
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-visaro-orange transition-colors duration-200 self-start"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to results
        </button>

        <div>
          <h1 className="text-2xl font-medium text-deepBlue">Review &amp; Book</h1>
          <p className="text-gray-500 flex items-center gap-1.5">
            <span className="font-semibold">{slice?.origin}</span>
            <AirplaneInFlight size={14} weight="fill" className="text-visaro-orange" />
            <span className="font-semibold">{slice?.destination}</span>
            {firstSegment?.departure_at && (
              <span className="text-gray-400">· {moment(firstSegment.departure_at).format("D MMM YYYY, HH:mm")}</span>
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
          {/* Trip summary sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex flex-col gap-5 lg:sticky lg:top-6"
          >
            <div className={sidebarCardClass}>
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center size-9 rounded-full bg-deepBlue/5 text-deepBlue shrink-0">
                  <AirplaneInFlight size={18} weight="duotone" />
                </span>
                <h2 className="font-bold text-deepBlue">Your itinerary</h2>
              </div>
              <div className="flex flex-col gap-4">
                {(offer?.slices ?? []).map((leg, index) => (
                  <LegSummary
                    key={index}
                    slice={leg}
                    label={
                      (offer?.slices?.length ?? 0) > 1 ? (index === 0 ? "Outbound" : "Return") : undefined
                    }
                  />
                ))}
              </div>
            </div>

            {offerError && (
              <div className="flex items-center gap-2 text-red-600 text-sm border border-red-200 bg-red-50 rounded-xl p-4">
                <Warning size={18} className="shrink-0" />
                <span>
                  Unable to refresh this offer.{" "}
                  <button type="button" className="underline font-medium" onClick={() => refreshOffer()}>
                    Try again
                  </button>
                </span>
              </div>
            )}

            {offer && (
              <div className={sidebarCardClass}>
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-deepBlue">Price breakdown</h2>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-visaro-visaGreen">
                    <SealCheck size={12} weight="fill" />
                    Revalidated
                  </span>
                </div>
                <div className="flex flex-col gap-2.5 text-sm">
                  {baseAmount !== null && baseAmount !== undefined && (
                    <div className="flex items-center justify-between text-gray-500">
                      <span>Base fare</span>
                      <span>{offer.currency} {formatAsMoney(baseAmount)}</span>
                    </div>
                  )}
                  {taxAmount !== null && taxAmount !== undefined && (
                    <div className="flex items-center justify-between text-gray-500">
                      <span>Taxes &amp; fees</span>
                      <span>{offer.currency} {formatAsMoney(taxAmount)}</span>
                    </div>
                  )}
                  <div className="h-px bg-gray-100 my-1" />
                  <div className="flex items-center justify-between font-bold text-deepBlue text-base">
                    <span>Total</span>
                    <span className="font-aeonic">
                      {offer.currency} {offer.total_amount !== null ? formatAsMoney(offer.total_amount) : "—"}
                    </span>
                  </div>
                  {offer.quote && (
                    <div className="flex items-center justify-between text-gray-400 text-xs">
                      <span>Approx. in Naira</span>
                      <span>≈ ₦{formatAsMoney(offer.quote.amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={sidebarCardClass}>
              {trustPoints.map((point) => (
                <div key={point.text} className="flex items-center gap-3">
                  <span className="flex items-center justify-center size-8 rounded-full bg-visaro-orange/10 text-visaro-orange shrink-0">
                    <point.icon size={16} weight="fill" />
                  </span>
                  <span className="text-sm text-gray-600 font-medium">{point.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Passenger form / payment method */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 }}
          >
            {isCreateOrderError && (
              <div className="flex items-center gap-2 text-red-600 text-sm border border-red-200 bg-red-50 rounded-xl p-4 mb-5">
                <Warning size={18} className="shrink-0" />
                {(createOrderError as any)?.message || "Booking failed. Please try again."}
              </div>
            )}

            {step === "payment" && passengerValues ? (
              <PaymentMethodStep
                offer={offer}
                walletBalance={parseWalletBalance(user?.wallet?.available_balance)}
                paymentMethod={paymentMethod}
                onSelectPaymentMethod={setPaymentMethod}
                onBack={() => setStep("passengers")}
                onConfirm={() => {
                  if (!offer) return;
                  createOrder({ offer_id: offer.id, payment_method: paymentMethod, passengers: passengerValues });
                }}
                confirming={creatingOrder}
                payIn3Enabled={payIn3Enabled}
                offerRefreshing={isRefreshingOffer || !!offerError}
                onKycLeave={() => {
                  if (!user?.id || !initialOffer?.id) return;
                  kycDraft = {
                    userId: String(user.id),
                    offerId: initialOffer.id,
                    passengers: passengerValues,
                    paymentMethod,
                    expiresAt: Date.now() + 30 * 60_000,
                  };
                }}
              />
            ) : (
            <Formik<FormValues>
              enableReinitialize
              validationSchema={validationSchema}
              initialValues={{
                passengers: passengerValues ?? passengerPlaceholders.map((placeholder) => ({
                  id: placeholder.id ?? "",
                  given_name: "",
                  family_name: "",
                  born_on: "",
                  gender: "m",
                  title: "mr",
                  email: "",
                  phone_number: "",
                })),
              }}
              onSubmit={(values) => {
                setPassengerValues(values.passengers);
                setStep("payment");
              }}
            >
              {({ values, errors, touched }) => (
                <Form className="flex flex-col gap-5">
                  <FieldArray name="passengers">
                    {() =>
                      values.passengers.map((_, index) => {
                        const passengerErrors = (errors.passengers?.[index] ?? {}) as Record<string, string>;
                        const passengerTouched = (touched.passengers?.[index] ?? {}) as Record<string, boolean>;
                        const hasError = (field: string) => Boolean(passengerTouched[field] && passengerErrors[field]);

                        return (
                          <div
                            key={index}
                            className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] border border-gray-100 p-5 flex flex-col gap-4"
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="flex items-center justify-center size-8 rounded-full bg-deepBlue text-white text-sm font-bold shrink-0">
                                {index + 1}
                              </span>
                              <h2 className="font-bold text-deepBlue">Passenger {index + 1}</h2>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>Title</label>
                                <Field as="select" name={`passengers.${index}.title`} className={fieldClass()}>
                                  <option value="mr">Mr</option>
                                  <option value="mrs">Mrs</option>
                                  <option value="ms">Ms</option>
                                </Field>
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>Gender</label>
                                <Field as="select" name={`passengers.${index}.gender`} className={fieldClass()}>
                                  <option value="m">Male</option>
                                  <option value="f">Female</option>
                                </Field>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>First name</label>
                                <Field
                                  name={`passengers.${index}.given_name`}
                                  className={fieldClass(hasError("given_name"))}
                                />
                                <ErrorMessage name={`passengers.${index}.given_name`} component="div" className="text-red-500 text-xs font-medium" />
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>Last name</label>
                                <Field
                                  name={`passengers.${index}.family_name`}
                                  className={fieldClass(hasError("family_name"))}
                                />
                                <ErrorMessage name={`passengers.${index}.family_name`} component="div" className="text-red-500 text-xs font-medium" />
                              </div>
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className={fieldLabelClass}>Date of birth</label>
                              <Field
                                type="date"
                                name={`passengers.${index}.born_on`}
                                max={moment().format("YYYY-MM-DD")}
                                className={fieldClass(hasError("born_on"))}
                              />
                              <ErrorMessage name={`passengers.${index}.born_on`} component="div" className="text-red-500 text-xs font-medium" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>Email</label>
                                <Field
                                  type="email"
                                  name={`passengers.${index}.email`}
                                  className={fieldClass(hasError("email"))}
                                />
                                <ErrorMessage name={`passengers.${index}.email`} component="div" className="text-red-500 text-xs font-medium" />
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <label className={fieldLabelClass}>Phone number</label>
                                <Field
                                  name={`passengers.${index}.phone_number`}
                                  placeholder="+234..."
                                  className={fieldClass(hasError("phone_number"))}
                                />
                                <ErrorMessage name={`passengers.${index}.phone_number`} component="div" className="text-red-500 text-xs font-medium" />
                              </div>
                            </div>
                          </div>
                        );
                      })
                    }
                  </FieldArray>

                  <button
                    type="submit"
                    disabled={!offer}
                    className="inline-flex items-center justify-center gap-2 bg-visaro-orange text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-visaro-orange/90 disabled:opacity-50 disabled:hover:bg-visaro-orange transition-colors duration-200"
                  >
                    Continue to Payment
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </Form>
              )}
            </Formik>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReviewAndBook;
